import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const RrppTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [error, setError] = useState("");
  const [rrppList, setRrppList] = useState([]);
  const [showRrppForm, setShowRrppForm] = useState(false);
  const [rrppForm, setRrppForm] = useState({
    nombre: "",
    telefono: "",
    email: "",
    evento_id: ""
  });
  const [selectedEventForRrpp, setSelectedEventForRrpp] = useState("");

  const fetchRrpp = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/rrpp`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setRrppList(response.data);
    } catch (e) {
      console.error("Error fetching RRPP:", e);
    }
  }, [credentials]);

  useEffect(() => {
    fetchRrpp();
  }, [fetchRrpp]);

  return (
    <>
          <>
            {/* Add RRPP Button */}
            {!showRrppForm && (
              <button 
                onClick={() => {
                  setShowRrppForm(true);
                  setRrppForm({ nombre: "", telefono: "", email: "", evento_id: "" });
                }}
                className="btn-primary mb-8 w-auto px-6"
                data-testid="add-rrpp-btn"
              >
                <UserPlus size={18} className="inline mr-2" />
                Nuevo RRPP
              </button>
            )}

            {/* RRPP Form */}
            {showRrppForm && (
              <div className="form-section mb-8">
                <button 
                  onClick={() => setShowRrppForm(false)}
                  className="text-[var(--text-muted)] hover:text-white mb-4 flex items-center gap-2"
                >
                  <ChevronLeft size={18} />
                  Volver
                </button>
                <h3 className="form-title mb-6">Nuevo RRPP (Relaciones Públicas)</h3>
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setError("");
                  try {
                    await axios.post(`${API}/admin/rrpp`, rrppForm, {
                      headers: { Authorization: `Basic ${credentials}` }
                    });
                    setShowRrppForm(false);
                    setRrppForm({ nombre: "", telefono: "", email: "", evento_id: "" });
                    fetchRrpp();
                  } catch (err) {
                    setError(err.response?.data?.detail || "Error al crear RRPP");
                  }
                }}>
                  <div className="form-group">
                    <label className="form-label">Nombre del RRPP</label>
                    <input
                      type="text"
                      className="custom-input"
                      placeholder="Nombre completo"
                      value={rrppForm.nombre}
                      onChange={(e) => setRrppForm(prev => ({ ...prev, nombre: e.target.value }))}
                      required
                      data-testid="rrpp-nombre"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Teléfono</label>
                      <input
                        type="tel"
                        className="custom-input"
                        placeholder="600 000 000"
                        value={rrppForm.telefono}
                        onChange={(e) => setRrppForm(prev => ({ ...prev, telefono: e.target.value }))}
                        data-testid="rrpp-telefono"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="custom-input"
                        placeholder="email@ejemplo.com"
                        value={rrppForm.email}
                        onChange={(e) => setRrppForm(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="rrpp-email"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Evento Asignado</label>
                    <select
                      className="custom-input"
                      value={rrppForm.evento_id}
                      onChange={(e) => setRrppForm(prev => ({ ...prev, evento_id: e.target.value }))}
                      required
                      data-testid="rrpp-evento"
                    >
                      <option value="">Selecciona un evento</option>
                      {events.filter(e => e.activo).map(event => (
                        <option key={event.id} value={event.id}>
                          {event.nombre} ({event.fecha})
                        </option>
                      ))}
                    </select>
                  </div>
                  <button 
                    type="submit" 
                    className="btn-primary w-full mt-4"
                    data-testid="save-rrpp-btn"
                  >
                    Crear RRPP y Generar Enlace
                  </button>
                </form>
              </div>
            )}

            {/* Filter by Event */}
            <div className="mb-6">
              <label className="form-label mb-2">Filtrar por Evento</label>
              <select
                className="custom-input max-w-md"
                value={selectedEventForRrpp}
                onChange={(e) => setSelectedEventForRrpp(e.target.value)}
              >
                <option value="">Todos los eventos</option>
                {events.filter(e => e.activo).map(event => (
                  <option key={event.id} value={event.id}>
                    {event.nombre} ({event.fecha})
                  </option>
                ))}
              </select>
            </div>

            {/* RRPP List */}
            <div>
              <h3 className="font-heading text-lg text-[var(--text-muted)] uppercase mb-4">
                Relaciones Públicas ({rrppList.filter(r => !selectedEventForRrpp || r.evento_id === selectedEventForRrpp).length})
              </h3>
              {rrppList.filter(r => !selectedEventForRrpp || r.evento_id === selectedEventForRrpp).length === 0 ? (
                <p className="text-[var(--text-muted)] text-center py-8">No hay RRPP registrados</p>
              ) : (
                <div className="grid gap-4">
                  {rrppList
                    .filter(r => !selectedEventForRrpp || r.evento_id === selectedEventForRrpp)
                    .map(rrpp => {
                      const shareUrl = `${window.location.origin}/?rrpp=${rrpp.code}`;
                      
                      return (
                        <div 
                          key={rrpp.id} 
                          className="admin-event-card"
                          data-testid={`rrpp-${rrpp.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className="w-14 h-14 bg-[var(--primary)] flex items-center justify-center">
                                <Share2 size={28} className="text-black" />
                              </div>
                              <div>
                                <h4 className="text-white font-bold text-lg">{rrpp.nombre}</h4>
                                <p className="text-[var(--accent-cyan)] text-sm font-mono">
                                  Código: {rrpp.code}
                                </p>
                                <p className="text-[var(--text-muted)] text-sm">
                                  Evento: {rrpp.evento_nombre}
                                </p>
                                {rrpp.telefono && (
                                  <p className="text-[var(--text-muted)] text-xs">📱 {rrpp.telefono}</p>
                                )}
                                {rrpp.email && (
                                  <p className="text-[var(--text-muted)] text-xs">📧 {rrpp.email}</p>
                                )}
                                
                                {/* Stats */}
                                <div className="flex gap-4 mt-3">
                                  <div className="text-center">
                                    <p className="text-2xl font-bold text-[var(--primary)]">{rrpp.total_reservas || 0}</p>
                                    <p className="text-xs text-[var(--text-muted)]">Reservas</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-2xl font-bold text-[var(--success)]">{rrpp.total_validadas || 0}</p>
                                    <p className="text-xs text-[var(--text-muted)]">Validadas</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(shareUrl);
                                  alert('Enlace copiado al portapapeles');
                                }}
                                className="px-4 py-2 bg-[var(--accent-cyan)] text-black font-bold text-sm flex items-center gap-2 hover:bg-[var(--accent-cyan)]/80 transition-colors"
                                data-testid={`copy-rrpp-${rrpp.id}`}
                              >
                                <Copy size={16} />
                                Copiar Enlace
                              </button>
                              <button
                                onClick={() => {
                                  window.open(`https://wa.me/?text=${encodeURIComponent(`¡Consigue tu entrada gratis! ${shareUrl}`)}`, '_blank');
                                }}
                                className="px-4 py-2 bg-green-500 text-white font-bold text-sm flex items-center gap-2 hover:bg-green-600 transition-colors"
                              >
                                <MessageCircle size={16} />
                                WhatsApp
                              </button>
                              <button 
                                onClick={async () => {
                                  if (window.confirm(`¿Eliminar RRPP ${rrpp.nombre}?`)) {
                                    try {
                                      await axios.delete(`${API}/admin/rrpp/${rrpp.id}`, {
                                        headers: { Authorization: `Basic ${credentials}` }
                                      });
                                      fetchRrpp();
                                    } catch (err) {
                                      setError("Error al eliminar RRPP");
                                    }
                                  }
                                }}
                                className="px-4 py-2 bg-[var(--error)] text-white font-bold text-sm flex items-center gap-2 hover:bg-[var(--error)]/80 transition-colors"
                              >
                                <Trash2 size={16} />
                                Eliminar
                              </button>
                            </div>
                          </div>
                          
                          {/* Share Link */}
                          <div className="mt-4 p-3 bg-black/30 border border-[var(--border-color)]">
                            <p className="text-[var(--text-muted)] text-xs mb-1">Enlace de seguimiento:</p>
                            <p className="text-[var(--accent-cyan)] text-sm font-mono break-all">{shareUrl}</p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </>

    </>
  );
};

export default RrppTab;
